import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { CiWallet } from "react-icons/ci";
import { FaEllipsisVertical } from "react-icons/fa6";
import { FaCube } from "react-icons/fa6";
import { NavLink, useNavigate, Link } from "react-router-dom";
import NIMC from "../assets/images/nimc.png";
import BVN_MODIFICATION from "../assets/images/BVN MODIFICATION.jpeg";
import BVN_USER from "../assets/images/BVN USER.jpeg";
import NIBSS from "../assets/images/nibss.png";
import CAC from "../assets/images/cac.png";
import BVN_CRM from "../assets/images/BVN-CRM.jpeg";
import Airtime from "../assets/images/airtime.png";
import Bank from "../assets/images/bank.webp";
import Data from "../assets/images/data.png";
import { IoClose } from "react-icons/io5";
import { FaRegCopy, FaCheck } from "react-icons/fa";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import "../assets/css/style.css";
import Logo from "../assets/images/Logo.png";
import Swal from "sweetalert2";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { config } from "../../config/config.jsx";
import CryptoJS from "crypto-js";
import ApiUserHome from "./ApiUserHome.jsx";
import { Megaphone, Smile, Sparkles } from "lucide-react";
import { Empty } from "antd";
import { EyeOutlined, SyncOutlined, InboxOutlined, DownloadOutlined } from "@ant-design/icons";
import {
  ArrowsUpDownIcon,
  ArrowUpIcon,
  ArrowDownIcon,
} from "@heroicons/react/24/solid";
import { useBVNSlip } from "../../context/BVNSlipContext";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import CoatofArm from "../assets/images/coat-of-arm.png";
import BVNlogo from "../assets/images/BVN-logo.png";
import AdvancedAvatar from "../assets/images/1.png";
import Fingerprint from "../assets/images/finger-print.png";


const SECRET_KEY = import.meta.env.VITE_APP_SECRET_KEY;

function encryptData(data) {
  return CryptoJS.AES.encrypt(JSON.stringify(data), SECRET_KEY).toString();
}

function decryptData(ciphertext) {
  if (!ciphertext) return null;
  try {
    const bytes = CryptoJS.AES.decrypt(ciphertext, SECRET_KEY);
    const decrypted = bytes.toString(CryptoJS.enc.Utf8);
    return JSON.parse(decrypted);
  } catch {
    return null;
  }
}

// DepositModal Component
function DepositModal({ open, onClose }) {
  const user = decryptData(localStorage.getItem("user"));
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("paystack");
  const [modalVisible, setModalVisible] = useState(false);

  // Add useEffect to handle animation
  useEffect(() => {
    if (open) {
      // Small delay to trigger animation
      setTimeout(() => setModalVisible(true), 10);
    } else {
      setModalVisible(false);
    }
  }, [open]);

  const handleClose = () => {
    setModalVisible(false);
    // Wait for animation to complete before closing
    setTimeout(() => onClose(), 300);
  };

  const handlePaystack = () => {
    if (!window.PaystackPop) {
      toast.error("Paystack is not loaded. Please refresh the page.");
      return;
    }

    const handler = window.PaystackPop.setup({
      key: import.meta.env.VITE_PAYSTACK_PUBLIC_URL,
      email: user?.email,
      amount: Number(amount) * 100,
      currency: "NGN",
      ref: `AY-${Date.now()}`,
      metadata: {
        userId: user?._id || user?.id,
        name: `${user?.firstName} ${user?.lastName}`,
      },
      callback: function (response) {
        toast.success("Payment successful. Processing...");
        // Handle successful payment here

        onClose();
      },
      onClose: function () {
        toast.error("Payment closed by user.");
      },
    });

    // Close the modal before opening the Paystack popup
    onClose();
    handler.openIframe();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!amount || !paymentMethod) {
      toast.error("Please complete all fields");
      return;
    }
    if (Number(amount) < 100) {
      toast.error("Minimum deposit amount is ₦100");
      return;
    }
    if (paymentMethod === "paystack") {
      handlePaystack();
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-center items-center bg-black/50">
      <div
        className={`bg-white rounded-xl w-[450px] max-w-[90%] p-6 shadow-xl 
          transform transition-all duration-300 
          ${modalVisible ? "scale-100 opacity-100" : "scale-90 opacity-0"}`}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-800">Make a Deposit</h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <IoClose size={24} />
          </button>
        </div>

        <p className="text-gray-600 mb-6">
          Add funds to your{" "}
          <span className="text-sky-700 font-bold">Fylder wallet.</span>
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="amount"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Amount
            </label>
            <input
              id="amount"
              type="number"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
              min="100"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent"
            />
          </div>

          <div>
            <label
              htmlFor="payment"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Payment Method
            </label>
            <select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent"
            >
              <option value="paystack">Paystack</option>
            </select>
          </div>

          {amount && (
            <div className="bg-gray-50 p-4 rounded-md">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Deposit Amount:</span>
                  <span>₦{Number(amount).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span>Processing Fee:</span>
                  <span>₦0.00</span>
                </div>
                <hr className="my-2" />
                <div className="flex justify-between font-bold">
                  <span>Total:</span>
                  <span>₦{Number(amount).toLocaleString()}</span>
                </div>
              </div>
            </div>
          )}

          <div className="flex space-x-3 mt-6">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-sky-500 text-white rounded-md hover:bg-sky-600"
            >
              Deposit
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ActivateWalletModal Component
function ActivateWalletModal({ open, onClose, onSubmit, loading }) {
  const [nin, setNin] = useState("");
  const [modalVisible, setModalVisible] = useState(false);

  useEffect(() => {
    if (open) {
      setTimeout(() => setModalVisible(true), 10);
    } else {
      setModalVisible(false);
    }
  }, [open]);

  const handleClose = () => {
    setModalVisible(false);
    setTimeout(() => {
      setNin("");
      onClose();
    }, 300);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!nin) {
      toast.error("Please enter your BVN");
      return;
    }
    if (nin.length !== 11) {
      toast.error("BVN must be exactly 11 digits");
      return;
    }
    onSubmit(nin);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-center items-center bg-slate-950/80 backdrop-blur-sm transition-all duration-300">
      <div
        className={`bg-[#0B0F19]/95 border border-slate-800/80 rounded-3xl w-[480px] max-w-[95%] p-6 md:p-8 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.7)] text-white relative overflow-hidden
          transform transition-all duration-300 
          ${modalVisible ? "scale-100 opacity-100 translate-y-0" : "scale-95 opacity-0 translate-y-4"}`}
      >
        {/* Glow Decor */}
        <div className="absolute top-[-50px] right-[-50px] w-40 h-40 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none"></div>

        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <div>
              <span className="text-[10px] font-bold text-emerald-400 uppercase tracking-[0.2em] block leading-none mb-1">Secure Activation</span>
              <h2 className="text-xl font-black tracking-tight text-white leading-none">Activate Wallet</h2>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-slate-400 hover:text-white p-2 rounded-xl bg-slate-900/60 border border-slate-800 hover:border-slate-700 transition-all duration-300 hover:rotate-90 focus:outline-none"
          >
            <IoClose size={20} />
          </button>
        </div>

        <p className="text-slate-400 mb-6 text-sm leading-relaxed">
          To verify your identity and activate your secure digital wallet, please enter your 11-digit Bank Verification Number (BVN).
        </p>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="relative">
            <label
              htmlFor="nin"
              className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2.5"
            >
              BVN Number
            </label>
            <input
              id="nin"
              type="text"
              pattern="[0-9]*"
              inputMode="numeric"
              maxLength="11"
              placeholder="00000000000"
              value={nin}
              onChange={(e) => setNin(e.target.value.replace(/\D/g, ""))}
              required
              className="w-full px-4 py-3.5 bg-slate-950/80 border border-slate-800/80 rounded-2xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent font-mono tracking-[0.3em] text-center text-xl font-bold text-white placeholder-slate-700 transition-all duration-300 shadow-[inset_0_2px_4px_rgba(0,0,0,0.6)]"
            />
          </div>

          <div className="flex space-x-3 pt-3">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 py-3 border border-slate-800 hover:border-slate-700 text-slate-300 hover:text-white rounded-2xl bg-slate-900/40 hover:bg-slate-900 font-bold text-sm transition-all duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white rounded-2xl font-bold text-sm transition-all duration-300 shadow-lg shadow-emerald-500/20 hover:shadow-emerald-400/30 flex items-center justify-center gap-2 disabled:from-slate-800 disabled:to-slate-800 disabled:text-slate-500 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Activating...</span>
                </>
              ) : (
                <span>Activate Wallet</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const shortenId = (id) => {
  if (!id || id === "N/A") return "N/A";
  const strId = String(id);
  return strId.length > 4 ? "•".repeat(strId.length - 4) + strId.slice(-4) : strId;
};

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    });
  } catch {
    return "N/A";
  }
};

function Dashboard() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const [isDepositModalOpen, setIsDepositModalOpen] = useState(false); // New state for deposit modal
  const [isActivateModalOpen, setIsActivateModalOpen] = useState(false); // State for wallet activation modal

  const [creatingAccount, setCreatingAccount] = useState(false);
  const [account, setAccount] = useState(null);
  const [loadingAccount, setLoadingAccount] = useState(true);
  const [introStatus, setIntroStatus] = useState([]);

  const user = decryptData(localStorage.getItem("user"));
  const firstName = user?.firstName || "User";
  const userId = user?._id || user?.id;



  // Detect if user is new or returning
  const [isReturning, setIsReturning] = useState(false);

  useEffect(() => {
    // Check localStorage for a flag
    const hasVisited = localStorage.getItem("hasVisitedAYCreative");
    if (hasVisited) {
      setIsReturning(true);
    } else {
      setIsReturning(false);
      localStorage.setItem("hasVisitedAYCreative", "true");
    }
  }, []);

  useEffect(() => {
    const fetchIntro = async () => {
      try {
        const response = await axios.get(
          `${config.apiBaseUrl}/${config.endpoints.userIntro}`,
          { withCredentials: true }
        );
        setIntroStatus(response.data?.data || []);
      } catch (error) {
        
      }
    };
    fetchIntro();
  }, []);

  // Load Paystack script
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://js.paystack.co/v1/inline.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  const handleLogout = () => {
    localStorage.removeItem("user");
    navigate("/login");
  };

  const fetchBalance = async () => {
    if (!userId) return;
    setLoadingAccount(true);
    try {
      const accountRes = await axios.get(
        `${config.apiBaseUrl}${config.endpoints.walletBalance}${userId}`
      );
      
      setAccount(accountRes.data.wallet);
    } catch (err) {
      setAccount(null);
    } finally {
      setLoadingAccount(false);
    }
  };

  const handleCreateWallet = async (nin) => {
    if (!userId) {
      toast.error("User not found. Please log in again.");
      return;
    }
    setCreatingAccount(true);
    try {
      const response = await axios.post(
        `${config.apiBaseUrl}${config.endpoints.initiateTopup}${userId}`,
        { number: nin }
      );
      

      const resData = response.data?.data || response.data?.account || response.data;
      
      let htmlContent = `<div class="text-left space-y-3 mt-4 text-slate-700">`;
      if (resData?.accountNumber || resData?.account_number) {
         htmlContent += `<div class="bg-slate-50 p-3 rounded-lg border border-slate-100"><p class="text-xs text-slate-500 uppercase tracking-wider mb-1">Account Number</p><p class="font-bold text-lg text-slate-800">${resData.accountNumber || resData.account_number}</p></div>`;
      }
      if (resData?.bankName || resData?.bank_name || resData?.bank) {
         htmlContent += `<div class="bg-slate-50 p-3 rounded-lg border border-slate-100"><p class="text-xs text-slate-500 uppercase tracking-wider mb-1">Bank Name</p><p class="font-bold text-lg text-slate-800">${resData.bankName || resData.bank_name || resData.bank}</p></div>`;
      }
      if (resData?.accountName || resData?.account_name) {
         htmlContent += `<div class="bg-slate-50 p-3 rounded-lg border border-slate-100"><p class="text-xs text-slate-500 uppercase tracking-wider mb-1">Account Name</p><p class="font-bold text-lg text-slate-800">${resData.accountName || resData.account_name}</p></div>`;
      }
      htmlContent += `</div>`;
      
      // Fallback message if we couldn't parse the specific bank details
      const isHtmlEmpty = htmlContent === `<div class="text-left space-y-3 mt-4 text-slate-700"></div>`;
      const fallbackMsg = response.data?.message || "Your virtual account has been successfully created.";

      Swal.fire({
        title: "Wallet Activated!",
        html: isHtmlEmpty ? `<p class="text-slate-600">${fallbackMsg}</p>` : htmlContent,
        icon: "success",
        confirmButtonColor: "#10b981",
        confirmButtonText: "Awesome!",
        customClass: {
          popup: "rounded-2xl",
          title: "text-2xl font-black text-slate-800",
        }
      });

      setIsActivateModalOpen(false);
      await fetchBalance();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to create wallet.");
    } finally {
      setCreatingAccount(false);
    }
  };

  useEffect(() => {
    fetchBalance();
  }, [userId]);

  const navItems = [
    {
      id: 1,
      name: "NIN VERIFY",
      icon: NIMC,
      to: "/dashboard/verifications/nin",
    },
    {
      id: 3,
      name: "IPE CLEARANCE",
      icon: NIMC,
      to: "/dashboard/ipe-clearance",
    },

    {
      id: 4,
      name: "MODIFICATION",
      icon: NIMC,
      to: "/dashboard/modification",
    },
    {
      id: 5,
      name: "PERSONALIZATION",
      icon: NIMC,
      to: "/dashboard/personalisation",
    },

    {
      id: 7,
      name: "VALIDATION",
      icon: NIMC,
      to: "/dashboard/validation",
    },
    {
      id: 8,
      name: "BVN MODIFICATION",
      icon: BVN_MODIFICATION,
      to: "/dashboard/bvn-modification",
    },
    {
      id: 9,
      name: "BVN VERIFY",
      icon: NIBSS,
      to: "/dashboard/verifications/bvn",
    },

    {
      id: 12,
      name: "CAC REGISTRATION",
      icon: CAC,
      to: "/dashboard/cac",
    },
    {
      id: 13,
      name: "BVN USER REGISTRATION",
      icon: BVN_USER,
      to: "/dashboard/bvn-user",
    },
    {
      id: 14,
      name: "BVN CRM",
      icon: BVN_CRM,
      to: "/dashboard/bvn-crm",
    },
  ];

  const [showBalance, setShowBalance] = useState(true);
  const { viewSlip } = useBVNSlip();
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [verificationType, setVerificationType] = useState("all");
  const [sortConfig, setSortConfig] = useState({
    key: "createdAt",
    direction: "desc",
  });
  const [isHistoryModalVisible, setIsHistoryModalVisible] = useState(false);
  const [selectedHistoryTransaction, setSelectedHistoryTransaction] = useState(null);
  const [historyPageSize, setHistoryPageSize] = useState(10);
  const [historyCurrentPage, setHistoryCurrentPage] = useState(1);
  const [copiedAccount, setCopiedAccount] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);

  const handleCopyAccount = (accNo) => {
    if (!accNo) return;
    navigator.clipboard?.writeText(accNo);
    setCopiedAccount(true);
    toast.success("Account number copied!");
    setTimeout(() => setCopiedAccount(false), 2000);
  };

  const fetchVerificationHistory = async () => {
    if (!userId) return;

    setHistoryLoading(true);
    try {
      const response = await axios.get(
        `${config.apiBaseUrl}${config.endpoints.DataHistory}${userId}`,
        {
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

     
      setHistoryData(response.data?.findData || []);
    } catch (error) {
      
    } finally {
      setHistoryLoading(false);
    }
  };

  const filteredHistoryTransactions = historyData.filter((transaction) => {
    if (verificationType === "all") return true;
    return transaction.dataFor === verificationType;
  });

  const verificationTypes = React.useMemo(() => {
    const types = [...new Set(historyData.map((item) => item.dataFor))].filter(
      Boolean
    );
    return types.sort();
  }, [historyData]);

  const sortData = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getSortedData = (data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      if (sortConfig.key === "createdAt") {
        const dateA = new Date(a[sortConfig.key]);
        const dateB = new Date(b[sortConfig.key]);
        return sortConfig.direction === "asc" ? dateA - dateB : dateB - dateA;
      }

      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  };

  const TableHeader = ({ label, sortKey, className }) => {
    const isSorted = sortConfig.key === sortKey;
    const icon = isSorted ? (
      sortConfig.direction === "asc" ? (
        <ArrowUpIcon className="w-4 h-4 text-blue-600" />
      ) : (
        <ArrowDownIcon className="w-4 h-4 text-blue-600" />
      )
    ) : (
      <ArrowsUpDownIcon className="w-4 h-4 text-gray-400" />
    );

    return (
      <th
        scope="col"
        className={`${className} cursor-pointer hover:bg-gray-50`}
        onClick={() => sortKey && sortData(sortKey)}
      >
        <div className="flex items-center justify-between">
          <span>{label}</span>
          <span className="ml-2">{icon}</span>
        </div>
      </th>
    );
  };

  const [downloadingId, setDownloadingId] = useState(null);

  // Build Basic BVN slip HTML for off-screen rendering
  const buildBasicSlipHTML = (userData) => {
    const avatarSrc = userData.base64Image
      ? `data:image/jpeg;base64,${userData.base64Image}`
      : BVNlogo;

    return `
      <div style="background:white;padding:32px;width:850px;font-family:Arial,sans-serif;">
        <div style="display:flex;justify-content:center;align-items:center;gap:20px;">
          <img src="${CoatofArm}" alt="Coat of Arms" style="width:92px;height:60px;" crossorigin="anonymous" />
          <p style="font-size:18px;color:#4B5563;font-weight:bold;">Federal Republic of Nigeria</p>
          <img src="${BVNlogo}" alt="BVN logo" style="width:80px;height:48px;" crossorigin="anonymous" />
        </div>
        <h2 style="font-size:18px;color:#4B5563;font-weight:bold;text-align:center;margin:8px 0;">Verified BVN Details</h2>
        <div style="display:flex;justify-content:space-between;align-items:flex-start;">
          <div style="flex:1;">
            <div style="display:flex;gap:24px;margin-top:12px;">
              <div>
                ${[
                  ["First Name", userData.firstName],
                  ["Middle Name", userData.middleName],
                  ["Last Name", userData.lastName],
                  ["Date of Birth", userData.dateOfBirth],
                  ["Gender", userData.gender],
                ].map(([label, val]) => `
                  <div style="display:flex;margin-bottom:8px;">
                    <span style="color:#374151;font-weight:600;width:120px;font-size:11px;">${label}:</span>
                    <span style="color:#4B5563;font-size:11px;">${val || "-"}</span>
                  </div>
                `).join("")}
                <div style="margin-top:20px;display:flex;gap:24px;">
                  <div>
                    ${[
                      ["Marital Status", userData.maritalStatus],
                      ["Phone Number", userData.phoneNumber1],
                      ["Enrollment Institution", userData.enrollmentBank],
                      ["Origin State", userData.stateOfOrigin],
                      ["Residence State", userData.stateOfResidence],
                      ["Residential Address", userData.residentialAddress],
                    ].map(([label, val]) => `
                      <div style="display:flex;margin-bottom:8px;">
                        <span style="color:#374151;font-weight:600;width:120px;font-size:11px;">${label}:</span>
                        <span style="color:#4B5563;font-size:11px;">${val || "-"}</span>
                      </div>
                    `).join("")}
                  </div>
                  <div>
                    ${[
                      ["NIN", userData.nin],
                      ["Enrollment Branch", userData.enrollmentBranch],
                      ["Origin LGA", userData.lgaOfOrigin],
                      ["Residence LGA", userData.lgaOfResidence],
                    ].map(([label, val]) => `
                      <div style="display:flex;margin-bottom:8px;">
                        <span style="color:#374151;font-weight:600;width:120px;font-size:11px;">${label}:</span>
                        <span style="color:#4B5563;font-size:11px;">${val || "-"}</span>
                      </div>
                    `).join("")}
                  </div>
                </div>
              </div>
              <div style="display:flex;flex-direction:column;align-items:center;width:145px;">
                <img src="${avatarSrc}" alt="Profile" style="width:80px;height:100px;object-fit:cover;" crossorigin="anonymous" />
                <p style="text-align:center;font-size:10px;margin-top:8px;color:#374151;font-weight:600;">BVN</p>
                <p style="text-align:center;font-size:16px;margin-top:4px;color:#374151;font-weight:600;">${userData.bvn || "-"}</p>
              </div>
            </div>
          </div>
          <div style="flex:1;padding-left:16px;">
            <h4 style="color:#16A34A;font-size:18px;text-align:center;">Verified</h4>
            <p style="font-size:10px;color:#4B5563;text-align:center;">Please do note that:</p>
            <ol style="font-weight:600;font-size:10px;color:#4B5563;margin-top:8px;padding-left:16px;">
              <li style="margin-bottom:8px;">The information on this slip remains valid until altered/modified where necessary by an authorized body</li>
              <li style="margin-bottom:8px;">Any person/authority using the information should verify it at anyverify.com.ng or any other channel approved by the federal government of Nigeria.</li>
              <li style="margin-bottom:8px;">The information shown on this slip is valid for the lifetime of the holder and <span style="color:#EF4444;font-weight:bold;">DOES NOT EXPIRE</span>.</li>
              <li style="margin-bottom:8px;">AnyVerify should not be blamed for any unauthorized alteration/copy/erasure etc. done on the slip</li>
            </ol>
          </div>
        </div>
      </div>
    `;
  };

  // Build Advanced BVN slip HTML for off-screen rendering
  const buildAdvancedSlipHTML = (userData) => {
    const avatarSrc = userData.base64Image
      ? `data:image/jpeg;base64,${userData.base64Image}`
      : AdvancedAvatar;

    const issuedDate = userData.verification_details?.verified_at
      ? formatDate(userData.verification_details.verified_at)
      : "-";

    return `
      <div style="background:white;padding:28px 48px;width:672px;font-family:Arial,sans-serif;border:1px solid #e5e7eb;border-radius:8px;position:relative;">
        <div style="margin-bottom:16px;">
          <img src="${BVNlogo}" alt="BVN Logo" style="width:280px;height:160px;" crossorigin="anonymous" />
        </div>
        <div style="display:flex;align-items:flex-start;margin-bottom:16px;">
          <img src="${avatarSrc}" alt="User Avatar" style="width:160px;height:auto;margin-right:16px;" crossorigin="anonymous" />
          <div>
            <p style="color:#9CA3AF;font-size:17px;font-weight:600;">SURNAME</p>
            <p style="color:#111827;font-size:18px;font-weight:600;">${userData.lastName || "-"}</p>
            <p style="color:#9CA3AF;font-size:17px;font-weight:600;margin-top:12px;">FIRST NAME/ OTHER NAME</p>
            <p style="color:#111827;font-size:18px;font-weight:600;">${userData.firstName || "-"} ${userData.middleName || ""}</p>
            <div style="display:flex;gap:40px;margin-top:12px;">
              <div>
                <p style="color:#9CA3AF;font-size:16px;font-weight:600;">DATE OF BIRTH</p>
                <p style="color:#111827;font-size:18px;font-weight:600;">${userData.dateOfBirth || "-"}</p>
              </div>
              <div>
                <p style="color:#9CA3AF;font-size:16px;font-weight:600;">GENDER</p>
                <p style="color:#111827;font-size:18px;font-weight:600;">${userData.gender || "-"}</p>
              </div>
              <div>
                <p style="color:#9CA3AF;font-size:16px;font-weight:600;">ISSUED DATE</p>
                <p style="color:#111827;font-size:18px;font-weight:600;">${issuedDate}</p>
              </div>
            </div>
          </div>
        </div>
        <div style="text-align:center;margin-top:16px;">
          <p style="color:#9CA3AF;font-size:18px;font-weight:600;">Bank Verification Number (BVN)</p>
          <p style="color:#111827;font-size:30px;font-weight:600;">${userData.bvn || "-"}</p>
        </div>
        <div style="position:absolute;top:48px;right:0;display:flex;align-items:center;gap:24px;">
          <div>
            <img src="${Fingerprint}" alt="Fingerprint" style="width:168px;height:260px;margin-right:16px;" crossorigin="anonymous" />
          </div>
        </div>
      </div>
    `;
  };

  const handleDownloadSlip = async (transaction) => {
    const dataFor = transaction.dataFor;
    const slipType = transaction.slipLayout;
    
    if (dataFor === "IPE-Slip") {
      toast.warning("IPE Clearance documents cannot be downloaded directly.");
      return;
    }

    setDownloadingId(transaction._id);

    try {
      if (dataFor === "NIN-Slip") {
        // NIN slips have pregenerated PDF Base64
        let pdfBase64 = null;

        if (transaction.pdf_base64) {
          pdfBase64 = transaction.pdf_base64;
        } else if (transaction.data?.pdf_base64) {
          pdfBase64 = transaction.data.pdf_base64;
        } else if (transaction.data?.data?.pdf_base64) {
          pdfBase64 = transaction.data.data.pdf_base64;
        } else if (transaction.pdfBase64) {
          pdfBase64 = transaction.pdfBase64;
        } else if (transaction.data?.pdfBase64) {
          pdfBase64 = transaction.data.pdfBase64;
        }

        if (pdfBase64) {
          let cleanBase64 = pdfBase64.replace(/\s/g, "");
          if (cleanBase64.startsWith("data:")) {
            cleanBase64 = cleanBase64.split(",")[1];
          }

          const binaryString = window.atob(cleanBase64);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }

          const blob = new Blob([bytes], { type: "application/pdf" });
          const url = URL.createObjectURL(blob);

          const link = document.createElement("a");
          link.href = url;
          link.download = `NIN_Slip_${transaction.data?.user_data?.searchParameter || transaction.data?.user_data?.nin || "slip"}-${Date.now()}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          toast.success("NIN Slip downloaded successfully!");
        } else {
          toast.error("PDF document is not available for immediate download.");
        }
      } else if (dataFor === "BVN-Slip") {
        // BVN slips render via html2canvas & jsPDF
        const userData = transaction.data?.data;
        if (!userData) {
          toast.error("BVN data is missing or incomplete.");
          setDownloadingId(null);
          return;
        }

        const slipHTML = slipType === "Basic"
          ? buildBasicSlipHTML(userData)
          : buildAdvancedSlipHTML(userData);

        const iframe = document.createElement("iframe");
        iframe.style.cssText = "position:fixed;left:-9999px;top:-9999px;width:1800px;height:1200px;border:none;visibility:hidden;";
        document.body.appendChild(iframe);
        iframe.setAttribute("width", "1800");
        iframe.setAttribute("height", "1200");

        const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
        iframeDoc.open();
        iframeDoc.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <style>
                * { margin: 0; padding: 0; box-sizing: border-box; }
                html, body { background: white; font-family: Arial, sans-serif; width: 100%; }
              </style>
            </head>
            <body>${slipHTML}</body>
          </html>
        `);
        iframeDoc.close();

        // Wait for images inside the iframe to load
        const images = iframeDoc.querySelectorAll("img");
        await Promise.all(
          Array.from(images).map(
            (img) =>
              new Promise((resolve) => {
                if (img.complete) resolve();
                else {
                  img.onload = resolve;
                  img.onerror = resolve;
                }
              })
          )
        );

        await new Promise((resolve) => setTimeout(resolve, 400));

        const targetEl = iframeDoc.body.firstElementChild;
        const elWidth = targetEl.scrollWidth;
        const elHeight = targetEl.scrollHeight;

        const canvas = await html2canvas(targetEl, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: "#ffffff",
          width: elWidth,
          height: elHeight,
          windowWidth: elWidth + 100,
          windowHeight: elHeight + 100,
        });

        const imgData = canvas.toDataURL("image/png");
        const canvasW = canvas.width;
        const canvasH = canvas.height;

        const pxToMm = 25.4 / 96;
        const pdfWidth = (canvasW / 2) * pxToMm + 10;
        const pdfHeight = (canvasH / 2) * pxToMm + 10;

        const pdf = new jsPDF({
          orientation: pdfWidth > pdfHeight ? "landscape" : "portrait",
          unit: "mm",
          format: [pdfWidth, pdfHeight],
        });

        pdf.addImage(imgData, "PNG", 5, 5, pdfWidth - 10, pdfHeight - 10);

        const bvn = userData.bvn || "unknown";
        const firstName = userData.firstName || "";
        const lastName = userData.lastName || "";
        const fileName = `BVN_Slip_${firstName}_${lastName}_${bvn}.pdf`;
        pdf.save(fileName);

        if (iframe && iframe.parentNode) {
          iframe.parentNode.removeChild(iframe);
        }
        toast.success("BVN Slip downloaded successfully!");
      }
    } catch (error) {
      
      toast.error("Failed to generate and download slip document.");
    } finally {
      setDownloadingId(null);
    }
  };

  const handleRetryVerification = async (transaction) => {
    const result = await Swal.fire({
      title: "Confirm IPE Clearance",
      html: `
        <p class="mb-2 text-gray-500">Please confirm your details:</p>
        <p class="mb-2 text-gray-500">Tracking ID: ${transaction.trackingId}</p>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#f59e0b",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, proceed",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    setHistoryLoading(true);
    const payload = {
      trackingId: transaction.trackingId,
      userId,
    };

    try {
      const response = await axios.post(
        `${config.apiBaseUrl}${config.endpoints.checkStatusipe}`,
        payload,
        { withCredentials: true }
      );

      toast.success("IPE Clearance verified successfully!");
      fetchVerificationHistory();
    } catch (error) {
      toast.error(error.response?.data?.message || "Verification failed");
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    fetchVerificationHistory();
  }, [userId]);

  const sortedTransactions = getSortedData(filteredHistoryTransactions);
  const paginatedTransactions = sortedTransactions.slice(
    (historyCurrentPage - 1) * historyPageSize,
    historyCurrentPage * historyPageSize
  );
  const totalPages = Math.ceil(sortedTransactions.length / historyPageSize);

  if (user?.isApiUser) {
    return <ApiUserHome user={user} userId={userId} />;
  }

  return (
    <div className="max-w-[1500px] mx-auto overflow-x-hidden md:overflow-x-visible pt-1 sm:pt-2">
      {/* WALLET SECTION */}
      <div className="mb-4 sm:mb-6 w-full">
        {/* WALLET CARD */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#1E62F9] via-[#1A5AE5] to-[#1447BF] text-white p-4.5 sm:p-5 shadow-xl shadow-blue-500/15 flex flex-col justify-between min-h-[175px]">
          {/* Background circles */}
          <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-white/10 blur-2xl pointer-events-none" />
          <div className="absolute -bottom-20 -left-10 w-64 h-64 rounded-full bg-blue-400/20 blur-xl pointer-events-none" />
          <div className="absolute bottom-6 right-24 w-32 h-32 rounded-full border border-white/10 pointer-events-none" />

          <div className="relative z-10 flex flex-col justify-between h-full">
            {/* Header row: WALLET BALANCE label & Glassmorphic Wallet Icon Badge */}
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold tracking-wider text-blue-100 uppercase">
                WALLET BALANCE
              </span>

              <div className="w-8 h-8 rounded-xl bg-white/15 backdrop-blur-md flex items-center justify-center border border-white/20 shadow-inner">
                <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" />
                  <path d="M3 5v14a2 2 0 0 0 2 2h16v-5" />
                  <path d="M18 12a2 2 0 0 0 0 4h4v-4Z" />
                </svg>
              </div>
            </div>

            {/* Balance & Toggle Row */}
            <div className="my-1.5 sm:my-2">
              <div className="flex items-center gap-2.5">
                <span className="text-2xl sm:text-3xl font-extrabold tracking-tight">
                  {loadingAccount ? (
                    "Loading..."
                  ) : showBalance ? (
                    `₦${account?.balance !== undefined && account?.balance !== null
                      ? Number(account.balance).toLocaleString("en-NG", { minimumFractionDigits: 1, maximumFractionDigits: 2 })
                      : (!account?.accountNumber ? "0.0" : "11,110.0")}`
                  ) : (
                    "••••••••"
                  )}
                </span>
                <button
                  onClick={() => setShowBalance(!showBalance)}
                  title={showBalance ? "Hide Balance" : "Show Balance"}
                  className="p-1 rounded-full bg-white/10 hover:bg-white/20 text-white/90 transition backdrop-blur-xs focus:outline-none"
                  type="button"
                >
                  {showBalance ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
                </button>
              </div>

              {/* Virtual Account Details Pill */}
              <div className="mt-2 inline-flex items-center flex-wrap gap-1.5 sm:gap-2 text-[11px] sm:text-xs bg-white/15 backdrop-blur-md px-3 py-1 rounded-full border border-white/20 text-white">
                <span className="text-blue-100">
                  Bank: <strong className="text-white font-semibold">{account?.bankName || account?.bank_name || account?.bank || "PalmPay"}</strong>
                </span>
                <span className="text-white/40">|</span>
                <span className="text-blue-100">
                  Acct No: <strong className="text-white font-semibold tracking-wider">{account?.accountNumber || account?.account_number || "6608066419"}</strong>
                </span>
                <button
                  onClick={() => handleCopyAccount(account?.accountNumber || account?.account_number || "6608066419")}
                  className="ml-1 p-0.5 hover:text-cyan-200 transition flex items-center gap-1 text-[11px] font-medium text-white focus:outline-none"
                  title="Copy Account Number"
                  type="button"
                >
                  {copiedAccount ? (
                    <span className="text-emerald-300 flex items-center gap-0.5 font-bold">
                      <FaCheck size={10} /> Copied!
                    </span>
                  ) : (
                    <FaRegCopy size={11} />
                  )}
                </button>
              </div>
            </div>

            {/* Action Buttons (+ Fund Wallet & History) */}
            <div className="pt-1.5 grid grid-cols-2 gap-2.5 sm:gap-4 sm:max-w-sm">
              <button
                onClick={() => {
                  if (!account?.accountNumber && !loadingAccount) {
                    setIsActivateModalOpen(true);
                  } else {
                    setIsDepositModalOpen(true);
                  }
                }}
                className="flex items-center justify-center gap-1.5 py-2 sm:py-2.5 px-3 sm:px-4 bg-white text-blue-700 font-bold rounded-xl hover:bg-blue-50 transition shadow-md active:scale-[0.98] text-xs sm:text-sm"
                type="button"
              >
                <svg className="w-3.5 h-3.5 stroke-[3]" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
                <span>Fund Wallet</span>
              </button>

              <button
                onClick={() => setIsHistoryModalOpen(true)}
                className="flex items-center justify-center gap-1.5 py-2 sm:py-2.5 px-3 sm:px-4 bg-white/20 hover:bg-white/25 text-white font-semibold rounded-xl backdrop-blur-md border border-white/25 transition active:scale-[0.98] text-xs sm:text-sm"
                type="button"
              >
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                <span>History</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Announcement Banner */}
      <div className="bg-emerald-50/80 border border-emerald-200/90 rounded-2xl p-3 sm:px-4 flex items-center gap-3 text-emerald-800 text-sm shadow-xs mb-6">
        <div className="w-7 h-7 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center flex-shrink-0">
          <Megaphone className="w-4 h-4 stroke-[2.5]" />
        </div>
        <div className="flex-1 text-xs sm:text-sm font-medium">
          {introStatus.length > 0 && introStatus[0]?.message ? (
            <span dangerouslySetInnerHTML={{ __html: introStatus[0]?.message }}></span>
          ) : (
            "System Upgrade Complete: NIN verification and IPE Clearance processing speed is now 3x faster!"
          )}
        </div>
      </div>

      {/* Products / Services Cards */}
      <div className="grid grid-cols-3 md:grid-cols-4 gap-2.5 sm:gap-4 md:gap-4 lg:gap-5 w-full py-2 sm:py-3 mb-6">
        {navItems.map((item) => (
          <NavLink
            to={item.to}
            key={item.id}
            className="bg-white rounded-xl sm:rounded-2xl shadow-sm hover:shadow-md border border-slate-100 hover:border-blue-200 transition-all duration-200 p-3 sm:p-5 flex flex-col items-center justify-center text-center overflow-hidden hover:-translate-y-0.5 group"
          >
            <img
              src={item.icon}
              alt={item.name}
              className="w-11 h-11 sm:w-14 sm:h-14 md:w-16 md:h-16 object-contain mb-2 sm:mb-2.5 group-hover:scale-105 transition-transform duration-200"
            />
            <span className="text-[11px] xs:text-xs sm:text-xs md:text-sm font-semibold text-slate-700 group-hover:text-blue-600 leading-tight tracking-tight break-words w-full transition-colors">
              {item.name}
            </span>
          </NavLink>
        ))}
      </div>

      {/* Verifications History Section */}
      <div id="verifications-history" className="mt-8 bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden w-full max-w-[calc(100vw-2rem)] sm:max-w-full">
        <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-50 flex items-center justify-between flex-wrap gap-4">
          <div>
            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <span className="p-1.5 bg-indigo-50 text-indigo-600 rounded-lg">
                <CiWallet size={20} className="stroke-[0.5]" />
              </span>
              Verifications History
            </h3>
            <p className="text-xs text-gray-400 mt-1">Tracks your identity, background check, and slip records</p>
          </div>
          
          <div className="flex items-center gap-3 flex-wrap">
            {/* Filter Tabs */}
            <div className="flex bg-slate-100 p-1 rounded-xl gap-1">
              {[{ value: "all", label: "All" }, ...((verificationTypes.length > 0 ? verificationTypes : ["NIN-Slip", "BVN-Slip", "IPE-Slip"]).map(type => ({
                value: type,
                label: type === "NIN-Slip" ? "NIN" : type === "BVN-Slip" ? "BVN" : type === "IPE-Slip" ? "IPE" : type
              })))].map((tab) => (
                <button
                  key={tab.value}
                  onClick={() => {
                    setVerificationType(tab.value);
                    setHistoryCurrentPage(1);
                  }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all duration-200 uppercase ${
                    verificationType === tab.value
                      ? "bg-slate-800 text-white shadow-sm"
                      : "text-slate-500 hover:text-slate-800"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            {sortedTransactions.length > 0 && (
              <span className="text-xs font-semibold px-2.5 py-2 bg-slate-100 text-slate-600 rounded-xl">
                {sortedTransactions.length} total
              </span>
            )}
          </div>
        </div>

        <div className="p-3 sm:p-6 w-full max-w-full overflow-hidden">
          {/* Loading Spinner */}
          {historyLoading && (
            <div className="flex justify-center items-center h-48">
              <div className="flex flex-col items-center space-y-4">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-800"></div>
                <p className="text-slate-500 text-xs font-semibold">
                  Loading verification history...
                </p>
              </div>
            </div>
          )}

          {!historyLoading && sortedTransactions.length > 0 ? (
            <div className="relative overflow-hidden rounded-lg border border-gray-200 shadow w-full max-w-[calc(100vw-3.5rem)] sm:max-w-full">
              <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 max-w-full">
                
                {/* All Slips Unified Table */}
                {verificationType === "all" && (
                  <table className="w-full table-auto divide-y divide-gray-200 transition-all duration-300 ease-in-out min-w-[600px] sm:min-w-[800px] [&_th]:border [&_th]:border-gray-200 [&_td]:border [&_td]:border-gray-200 text-left text-sm">
                    <thead className="bg-gray-50 text-slate-500 text-xs font-bold uppercase tracking-wider">
                      <tr>
                        <TableHeader
                          label="Date"
                          sortKey="createdAt"
                          className="px-4 py-3"
                        />
                        <th className="px-4 py-3">Type</th>
                        <th className="px-4 py-3">ID / Parameter</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3 text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200 text-slate-600">
                      {paginatedTransactions.map((transaction, idx) => {
                        const isNIN = transaction.dataFor === "NIN-Slip";
                        const isBVN = transaction.dataFor === "BVN-Slip";
                        const isIPE = transaction.dataFor === "IPE-Slip";
                        
                        let param = "N/A";
                        if (isNIN) param = transaction.data?.user_data?.searchParameter || transaction.data?.user_data?.nin;
                        else if (isBVN) param = transaction.data?.data?.bvn;
                        else if (isIPE) param = transaction?.trackingId;

                        let status = "N/A";
                        if (isNIN) status = transaction.data?.user_data?.transactionStatus || transaction.data?.status;
                        else if (isBVN) status = transaction.data?.verification?.status;
                        else if (isIPE) status = transaction?.status;

                        return (
                          <tr
                            key={transaction._id || idx}
                            className="hover:bg-gray-50 transition-colors duration-200"
                          >
                            <td className="px-4 py-3 whitespace-nowrap">
                              {formatDate(transaction.createdAt)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">
                                {isNIN ? "NIN" : isBVN ? "BVN" : "IPE"}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap font-mono text-xs">
                              {shortenId(param)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${
                                ['success', 'successful', 'completed', 'verified'].includes(String(status || "").toLowerCase())
                                  ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                                  : ['pending'].includes(String(status || "").toLowerCase())
                                  ? "bg-amber-50 text-amber-700 border border-amber-100"
                                  : "bg-rose-50 text-rose-700 border border-rose-100"
                              }`}>
                                {status || "N/A"}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-center">
                              {!isIPE ? (
                                <button
                                  onClick={() => handleDownloadSlip(transaction)}
                                  disabled={downloadingId === transaction._id}
                                  className="text-blue-600 hover:text-blue-800 font-bold transition-all disabled:opacity-50 inline-flex items-center gap-1 bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded-xl text-xs border border-blue-200"
                                  title="Download PDF"
                                >
                                  {downloadingId === transaction._id ? (
                                    <>
                                      <span className="animate-spin w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full inline-block mr-1"></span>
                                      Downloading...
                                    </>
                                  ) : (
                                    <>
                                      <DownloadOutlined className="text-xs" />
                                      [download]
                                    </>
                                  )}
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleRetryVerification(transaction)}
                                  className="text-amber-500 hover:text-amber-700 transition-colors p-1"
                                  title="Re-check Status"
                                >
                                  <SyncOutlined className="text-lg hover:rotate-180 transition-transform duration-500" />
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}

                {/* NIN-Slip Table */}
                {verificationType === "NIN-Slip" && (
                  <table className="w-full table-auto divide-y divide-gray-200 transition-all duration-300 ease-in-out min-w-[600px] sm:min-w-[800px] [&_th]:border [&_th]:border-gray-200 [&_td]:border [&_td]:border-gray-200 text-left text-sm">
                    <thead className="bg-gray-50 text-slate-500 text-xs font-bold uppercase tracking-wider">
                      <tr>
                        <TableHeader
                          label="Date"
                          sortKey="createdAt"
                          className="px-4 py-3"
                        />
                        <th className="px-4 py-3">NIN Number</th>
                        <th className="px-4 py-3">Status</th>
                        <TableHeader
                          label="Card Type"
                          sortKey="dataFor"
                          className="px-4 py-3"
                        />
                        <th className="px-4 py-3 text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200 text-slate-600">
                      {paginatedTransactions.map((transaction, idx) => (
                        <tr
                          key={transaction._id || idx}
                          className="hover:bg-gray-50 transition-colors duration-200"
                        >
                          <td className="px-4 py-3 whitespace-nowrap">
                            {formatDate(transaction.createdAt)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap font-mono text-xs">
                            {shortenId(transaction.data?.user_data?.searchParameter || transaction.data?.user_data?.nin)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border ${
                              ['success', 'successful', 'completed', 'verified'].includes(String(transaction.data?.user_data?.transactionStatus || transaction.data?.status || "").toLowerCase())
                                ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                                : ['pending'].includes(String(transaction.data?.user_data?.transactionStatus || transaction.data?.status || "").toLowerCase())
                                ? "bg-amber-50 text-amber-700 border-amber-100"
                                : "bg-rose-50 text-rose-700 border-rose-100"
                            }`}>
                              {transaction.data?.user_data?.transactionStatus || transaction.data?.status || "N/A"}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
                              {transaction.dataFor} - {transaction.slipLayout}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-center">
                            <button
                              onClick={() => handleDownloadSlip(transaction)}
                              disabled={downloadingId === transaction._id}
                              className="text-blue-600 hover:text-blue-800 font-bold transition-all disabled:opacity-50 inline-flex items-center gap-1 bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded-xl text-xs border border-blue-200"
                              title="Download PDF"
                            >
                              {downloadingId === transaction._id ? (
                                <>
                                  <span className="animate-spin w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full inline-block mr-1"></span>
                                  Downloading...
                                </>
                              ) : (
                                <>
                                  <DownloadOutlined className="text-xs" />
                                  [download]
                                </>
                              )}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {/* BVN-Slip Table */}
                {verificationType === "BVN-Slip" && (
                  <table className="w-full table-auto divide-y divide-gray-200 transition-all duration-300 ease-in-out min-w-[600px] sm:min-w-[800px] [&_th]:border [&_th]:border-gray-200 [&_td]:border [&_td]:border-gray-200 text-left text-sm">
                    <thead className="bg-gray-50 text-slate-500 text-xs font-bold uppercase tracking-wider">
                      <tr>
                        <TableHeader
                          label="Date"
                          sortKey="createdAt"
                          className="px-4 py-3"
                        />
                        <th className="px-4 py-3">BVN Number</th>
                        <th className="px-4 py-3">Status</th>
                        <TableHeader
                          label="Card Type"
                          sortKey="dataFor"
                          className="px-4 py-3"
                        />
                        <th className="px-4 py-3 text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200 text-slate-600">
                      {paginatedTransactions.map((transaction, idx) => (
                        <tr
                          key={transaction._id || idx}
                          className="hover:bg-gray-50 transition-colors duration-200"
                        >
                          <td className="px-4 py-3 whitespace-nowrap">
                            {formatDate(transaction.createdAt)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap font-mono text-xs">
                            {shortenId(transaction.data?.data?.bvn)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border ${
                              ['success', 'successful', 'completed', 'verified'].includes(String(transaction.data?.verification?.status || "").toLowerCase())
                                ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                                : ['pending'].includes(String(transaction.data?.verification?.status || "").toLowerCase())
                                ? "bg-amber-50 text-amber-700 border-amber-100"
                                : "bg-rose-50 text-rose-700 border-rose-100"
                            }`}>
                              {transaction.data?.verification?.status || "N/A"}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
                              {transaction.dataFor} - {transaction.slipLayout}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-center">
                            <button
                              onClick={() => handleDownloadSlip(transaction)}
                              disabled={downloadingId === transaction._id}
                              className="text-blue-600 hover:text-blue-800 font-bold transition-all disabled:opacity-50 inline-flex items-center gap-1 bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded-xl text-xs border border-blue-200"
                              title="Download PDF"
                            >
                              {downloadingId === transaction._id ? (
                                <>
                                  <span className="animate-spin w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full inline-block mr-1"></span>
                                  Downloading...
                                </>
                              ) : (
                                <>
                                  <DownloadOutlined className="text-xs" />
                                  [download]
                                </>
                              )}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {/* IPE-Slip Table */}
                {verificationType === "IPE-Slip" && (
                  <table className="w-full table-auto divide-y divide-gray-200 transition-all duration-300 ease-in-out min-w-[600px] sm:min-w-[800px] [&_th]:border [&_th]:border-gray-200 [&_td]:border [&_td]:border-gray-200 text-left text-sm">
                    <thead className="bg-gray-50 text-slate-500 text-xs font-bold uppercase tracking-wider">
                      <tr>
                        <TableHeader
                          label="Date"
                          sortKey="createdAt"
                          className="px-4 py-3"
                        />
                        <th className="px-4 py-3">Old Tracking ID</th>
                        <TableHeader
                          label="Status"
                          sortKey="status"
                          className="px-4 py-3"
                        />
                        <th className="px-4 py-3">New Tracking ID</th>
                        <th className="px-4 py-3">NIN</th>
                        <th className="px-4 py-3 text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200 text-slate-600">
                      {paginatedTransactions.map((transaction, idx) => (
                        <tr
                          key={transaction._id || idx}
                          className="hover:bg-gray-50 transition-colors duration-200"
                        >
                          <td className="px-4 py-3 whitespace-nowrap">
                            {formatDate(transaction.createdAt)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap font-mono text-xs">
                            {shortenId(transaction?.trackingId)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${
                              String(transaction?.status || "").toLowerCase() === "pending"
                                ? "bg-amber-50 text-amber-700 border border-amber-100"
                                : String(transaction?.status || "").toLowerCase() === "success"
                                ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                                : "bg-rose-50 text-rose-700 border border-rose-100"
                            }`}>
                              {transaction?.status || "N/A"}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap font-mono text-xs">
                            {shortenId(transaction.data?.reply)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap font-mono text-xs">
                            {shortenId(transaction.data?.nin)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-center">
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() => handleRetryVerification(transaction)}
                                className="text-amber-500 hover:text-amber-700 transition-colors p-1"
                                title="Re-check Status"
                              >
                                <SyncOutlined className="text-lg hover:rotate-180 transition-transform duration-500" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

              </div>
            </div>
          ) : !historyLoading ? (
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <div className="w-16 h-16 bg-slate-50 text-slate-400 rounded-full flex items-center justify-center mb-4">
                <InboxOutlined size={32} className="stroke-[0.3]" />
              </div>
              <h4 className="text-sm font-semibold text-slate-700">No Verifications Found</h4>
              <p className="text-xs text-gray-400 mt-1 max-w-[280px]">
                Your {verificationType.replace("-Slip", "")} verification records will appear here as soon as you perform any checks.
              </p>
            </div>
          ) : null}

          {/* Pagination Controls - only show when not loading and has data */}
          {!historyLoading && sortedTransactions.length > 0 && (
            <div className="mt-4 flex flex-col lg:flex-row gap-4 items-center justify-between">
              {/* Rows per page and showing entries */}
              <div className="flex flex-col sm:flex-row items-center gap-2 w-full lg:w-auto">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-700 whitespace-nowrap">
                    Rows per page:
                  </span>
                  <select
                    value={historyPageSize}
                    onChange={(e) => {
                      setHistoryPageSize(Number(e.target.value));
                      setHistoryCurrentPage(1);
                    }}
                    className="border rounded-md px-2 py-1 text-sm transition-colors duration-200 ease-in-out hover:border-slate-500 focus:border-slate-500"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                </div>
                <span className="text-sm text-gray-500 font-semibold text-center sm:text-left">
                  Showing {(historyCurrentPage - 1) * historyPageSize + 1} to{" "}
                  {Math.min(historyCurrentPage * historyPageSize, sortedTransactions.length)} of{" "}
                  {sortedTransactions.length} entries
                </span>
              </div>

              {/* Pagination buttons */}
              <div className="flex flex-wrap items-center justify-center gap-2">
                <button
                  onClick={() => setHistoryCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={historyCurrentPage === 1}
                  className={`px-3 py-1.5 rounded-xl transition-all duration-200 ease-in-out font-bold text-xs ${
                    historyCurrentPage === 1
                      ? "bg-gray-100 text-gray-400"
                      : "bg-slate-800 text-white hover:bg-slate-700 shadow-sm"
                  }`}
                >
                  Previous
                </button>

                <div className="flex items-center gap-1">
                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i + 1}
                      onClick={() => setHistoryCurrentPage(i + 1)}
                      className={`px-3 py-1 rounded-lg font-bold text-xs transition-all duration-200 ${
                        historyCurrentPage === i + 1
                          ? "bg-slate-800 text-white"
                          : "bg-gray-100 text-slate-600 hover:bg-gray-200"
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>

                <button
                  onClick={() => setHistoryCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={historyCurrentPage === totalPages}
                  className={`px-3 py-1.5 rounded-xl transition-all duration-200 ease-in-out font-bold text-xs ${
                    historyCurrentPage === totalPages
                      ? "bg-gray-100 text-gray-400"
                      : "bg-slate-800 text-white hover:bg-slate-700 shadow-sm"
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Deposit Modal */}
      <DepositModal
        open={isDepositModalOpen}
        onClose={() => setIsDepositModalOpen(false)}
      />

      {/* Activate Wallet Modal */}
      <ActivateWalletModal
        open={isActivateModalOpen}
        onClose={() => setIsActivateModalOpen(false)}
        onSubmit={handleCreateWallet}
        loading={creatingAccount}
      />

      {/* Transaction History Quick Modal */}
      {isHistoryModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 shadow-2xl border border-slate-100">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-900">Transaction History</h3>
              <button 
                onClick={() => setIsHistoryModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 text-lg font-bold p-1 rounded-full hover:bg-slate-100 transition"
                type="button"
              >
                ✕
              </button>
            </div>

            <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
              {historyData && historyData.length > 0 ? (
                historyData.slice(0, 6).map((tx, idx) => {
                  const isCredit = tx.type === "credit" || Number(tx.amount || 0) > 0;
                  return (
                    <div key={idx} className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-100 text-xs">
                      <div>
                        <p className="font-semibold text-slate-800">{tx.dataFor || tx.title || "Verification"}</p>
                        <p className="text-slate-400">{formatDate(tx.createdAt)} • {tx.transactionReference || tx._id?.slice(-8) || "N/A"}</p>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold ${isCredit ? 'text-emerald-600' : 'text-slate-700'}`}>
                          {tx.amount ? `₦${Number(tx.amount).toLocaleString()}` : "Completed"}
                        </p>
                        <span className="text-[10px] text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded font-medium">Success</span>
                      </div>
                    </div>
                  );
                })
              ) : (
                [
                  { type: 'Wallet Funding', ref: 'TRX-948201', amount: '+₦5,000.00', date: 'Today, 09:20 AM', status: 'Success', isCredit: true },
                  { type: 'NIN Verification', ref: 'TRX-948190', amount: '-₦500.00', date: 'Yesterday, 04:15 PM', status: 'Success', isCredit: false },
                  { type: 'Referral Bonus', ref: 'REF-83192', amount: '+₦500.00', date: '28 Sep, 11:00 AM', status: 'Success', isCredit: true },
                  { type: 'IPE Clearance', ref: 'TRX-947812', amount: '-₦1,500.00', date: '26 Sep, 02:40 PM', status: 'Success', isCredit: false },
                ].map((tx, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-100 text-xs">
                    <div>
                      <p className="font-semibold text-slate-800">{tx.type}</p>
                      <p className="text-slate-400">{tx.date} • {tx.ref}</p>
                    </div>
                    <div className="text-right">
                      <p className={`font-bold ${tx.isCredit ? 'text-emerald-600' : 'text-slate-700'}`}>{tx.amount}</p>
                      <span className="text-[10px] text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded font-medium">{tx.status}</span>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="mt-5 flex gap-3">
              <button
                onClick={() => {
                  setIsHistoryModalOpen(false);
                  const el = document.getElementById("verifications-history");
                  if (el) el.scrollIntoView({ behavior: "smooth" });
                }}
                className="w-full py-2.5 rounded-xl border border-slate-200 text-slate-600 font-semibold hover:bg-slate-50 text-sm transition"
                type="button"
              >
                View Full Table
              </button>
              <button
                onClick={() => setIsHistoryModalOpen(false)}
                className="w-full py-2.5 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700 text-sm transition"
                type="button"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
}

export default Dashboard;
